"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";

export type SheetSide = "top" | "bottom";

interface SheetProps {
  mobileNavigation?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  title?: string;
  side?: SheetSide;
}

export function Sheet({ open, onOpenChange, children, title, side = "bottom", mobileNavigation = false }: SheetProps) {
  const [mounted, setMounted] = React.useState(false);
  const dialogRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => setMounted(true), []);

  React.useEffect(() => {
    if (open) {
      triggerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    }
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  React.useEffect(() => {
    if (!open) {
      triggerRef.current?.focus();
      return;
    }
    const dialog = dialogRef.current;
    if (!dialog) return;
    const focusable = dialog.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
    );
    focusable[0]?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onOpenChange(false);
        return;
      }
      if (event.key !== "Tab" || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onOpenChange, open]);

  if (!open || !mounted) return null;

  // Render via a portal to document.body so the fixed overlay fills the viewport.
  // The header uses backdrop-blur, which would otherwise become the containing
  // block for this fixed element and trap it inside the ~68px header.
  return createPortal(
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        aria-label="Close menu"
        data-nav-overlay={mobileNavigation ? "mobile" : undefined}
        className="absolute inset-0 bg-black/40"
        onClick={() => onOpenChange(false)}
      />
      <div
        ref={dialogRef}
        id={mobileNavigation ? "mobile-menu-panel" : undefined}
        data-nav-panel={mobileNavigation ? "mobile" : undefined}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={
          side === "top"
            ? "absolute left-0 right-0 top-0 max-h-[85vh] overflow-y-auto rounded-b-2xl bg-white p-4 shadow-xl motion-safe:animate-sheet-top md:left-auto md:right-0 md:top-0 md:h-full md:max-h-none md:w-80 md:rounded-none md:rounded-l-2xl md:motion-safe:animate-none"
            : "absolute bottom-0 left-0 right-0 max-h-[85vh] overflow-y-auto rounded-t-2xl bg-white p-4 shadow-xl motion-safe:animate-sheet-bottom md:left-auto md:right-0 md:top-0 md:h-full md:max-h-none md:w-80 md:rounded-none md:rounded-l-2xl md:motion-safe:animate-none"
        }
      >
        <div className="mb-4 flex items-center justify-between">
          {title && <h2 className="text-lg font-semibold">{title}</h2>}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Close"
            onClick={() => onOpenChange(false)}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}
